import React, { useEffect, useState } from 'react';
import { Button, Stack, TextField, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { SectionCard } from '../ui';
import { useToast } from '../../contexts/ToastContext';
import { useOrgFeatureFlag } from '../../hooks/useOrgFeatureFlags';
import { getAuthHeaders, makeApiCall } from '../../utils/apiConfig';
import { canGenerateCanonicalProductId, generateCanonicalProductId } from '../../utils/canonicalProductId';

interface StagedVariantLinkCardProps {
  productId: string;
  sku?: string;
  brand?: string;
  brandId?: string;
  parentSku?: string;
  variantGroupId?: string;
  onSaved: (product: { id: string; parentSku?: string; variantGroupId?: string }) => void;
}

/**
 * Admin control for linking a staged row into a variant family (MOT-820).
 * Hidden while `staged_variant_links` is off.
 */
export const StagedVariantLinkCard: React.FC<StagedVariantLinkCardProps> = ({
  productId,
  sku,
  brand,
  brandId,
  parentSku,
  variantGroupId,
  onSaved,
}) => {
  const { t } = useTranslation();
  const { showToast } = useToast();
  const { enabled, isLoading } = useOrgFeatureFlag('staged_variant_links');
  const [parentDraft, setParentDraft] = useState(parentSku || '');
  const [groupDraft, setGroupDraft] = useState(variantGroupId || '');
  const [pending, setPending] = useState(false);

  useEffect(() => {
    setParentDraft(parentSku || '');
    setGroupDraft(variantGroupId || '');
  }, [productId, parentSku, variantGroupId]);

  if (isLoading || !enabled) return null;

  const storedParent = (parentSku || '').trim();
  const storedGroup = (variantGroupId || '').trim();
  const dirty = parentDraft.trim() !== storedParent || groupDraft.trim() !== storedGroup;
  const brandKey = (brandId || brand || '').trim();
  const ownSku = (sku || '').trim();
  const canBeParent = canGenerateCanonicalProductId(brandKey, ownSku);

  const save = async (body: { parentSku: string | null; variantGroupId?: string | null }) => {
    setPending(true);
    try {
      const headers = await getAuthHeaders();
      const res = await makeApiCall(`/staged-products/${encodeURIComponent(productId)}`, {
        method: 'PATCH',
        headers: { ...headers, 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...body,
          editMeta: { source: 'admin_edit', summary: t('admin.stagedVariantLink.saved') },
        }),
      });
      const json = await res.json().catch(() => ({}));
      if (!res.ok) {
        const message = typeof json.error === 'string' ? json.error : t('admin.stagedVariantLink.failed');
        showToast(message, 'error');
        return;
      }
      if (json.data) onSaved(json.data);
      showToast(body.parentSku || body.variantGroupId ? t('admin.stagedVariantLink.saved') : t('admin.stagedVariantLink.cleared'), 'success');
    } catch {
      showToast(t('admin.stagedVariantLink.failed'), 'error');
    } finally {
      setPending(false);
    }
  };

  return (
    <SectionCard dense padded title={t('admin.stagedVariantLink.title')} sx={{ mb: 2 }}>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
        {t('admin.stagedVariantLink.body')}
      </Typography>
      <Stack spacing={1.5}>
        <TextField
          size="small"
          label={t('admin.stagedVariantLink.parentSku')}
          value={parentDraft}
          onChange={(event) => {
            const next = event.target.value;
            setParentDraft(next);
            // The stored group belongs to the previous parent. Blank it so a
            // save asks the server to fill the new parent's group.
            if (next.trim() !== storedParent && groupDraft.trim() === storedGroup) {
              setGroupDraft('');
            }
          }}
          helperText={t('admin.stagedVariantLink.parentHelp')}
          disabled={pending}
        />
        <TextField
          size="small"
          label={t('admin.stagedVariantLink.group')}
          value={groupDraft}
          onChange={(event) => setGroupDraft(event.target.value)}
          helperText={t('admin.stagedVariantLink.groupHelp')}
          disabled={pending}
        />
        <Stack direction="row" spacing={1} useFlexGap sx={{ flexWrap: 'wrap' }}>
          <Button
            size="small"
            variant="contained"
            disabled={!dirty || pending}
            onClick={() => {
              const parent = parentDraft.trim();
              const group = groupDraft.trim();
              // A blank group is a clear: with a parent, the server fills the
              // parent's group; with neither, both fields come off the row.
              void save({ parentSku: parent || null, variantGroupId: group || null });
            }}
          >
            {t('admin.stagedVariantLink.save')}
          </Button>
          {canBeParent && (
            <Button
              size="small"
              variant="outlined"
              disabled={pending}
              onClick={() => {
                setParentDraft('');
                setGroupDraft(generateCanonicalProductId(brandKey, ownSku));
              }}
            >
              {t('admin.stagedVariantLink.makeParent')}
            </Button>
          )}
          {(storedParent || storedGroup) && (
            <Button
              size="small"
              color="inherit"
              disabled={pending}
              onClick={() => void save({ parentSku: null, variantGroupId: null })}
            >
              {t('admin.stagedVariantLink.clear')}
            </Button>
          )}
        </Stack>
      </Stack>
    </SectionCard>
  );
};
